import { LitElement } from 'lit';

// import { choose } from 'lit/directives/choose.js';
import { until } from 'lit/directives/until.js';
import { live } from 'lit/directives/live.js';
import { ref } from 'lit/directives/ref.js';

import { msg, str } from '@lit/localize';
import { NorthStarElement, html, define } from '../../northstar';
import { observer as resizeObserver } from '../../utils/resize';
import { debounce, isTouch } from '../../utils';
import { setLocale, getLang } from '../../utils/locale';
import { NAMESPACE } from '../../constants';
import StatesMap from './states';
import './slider';
import '../icon';

import { createApi } from './api';

import styles from './calculator.css';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0, // 1500.99 => $1,501
});

const toUSD = (value) => currencyFormatter.format(+value);

function template(elem) {
  const { state, statesList: states } = elem;

  const scores = [
    { label: msg('Exceptional (750-850)'), value: msg('Exceptional (750-850)') },
    { label: msg('Very good (700-749)'), value: msg('Very good (700-749)') },
    { label: msg('Good (660-699)'), value: msg('Good (660-699)') },
    { label: msg('Fair (580-659)'), value: msg('Fair (580-659)') },
    { label: msg('Needs work (300-579)'), value: msg('Needs work (300-579)') },
    { label: msg('Unknown score'), value: msg('Unknown score') },
  ];

  const secureOptions = [
    { label: msg('Yes, I own a car without payments'), value: 'yes' },
    { label: msg('Yes, I’m still making payments'), value: 'has_car_payments' },
    { label: msg('No, I don’t own a car'), value: 'no' },
    { label: msg('No, I lease a car'), value: 'lease' },
  ];

  // ---- SAFE metadata getter (prevents unhandled rejections and undefined) ----
  let metadata;
  async function getMetaData() {
    if (metadata) return metadata;
    try {
      const m = await elem.getStateMetaData(state);
      metadata = m && typeof m === 'object' ? m : {};
    } catch (err) {
      metadata = {};
    }
    return metadata;
  }

  function onLoanDetailsChange(e) {
    const { target } = e;
    const { name, value } = target;
    elem.updateDetails({ [name]: value });

    elem.sendEvents(e, {
      [name]: value,
      event: 'change',
      target,
      ...target.dataset,
    });

    elem.dispatchEvent(new CustomEvent('calculator:field-change', {
      bubbles: true,
      composed: true,
      detail: { field: name, value },
    }));
  }

  function onLoanTypeChange(e) {
    const { target } = e;
    const name = target.getAttribute('data-name');
    const value = target.getAttribute('data-value');
    elem.updateDetails({ [name]: value });

    elem.sendEvents(e, {
      [name]: value,
      event: 'change',
      target,
      ...target.dataset,
    });
  }

  function onLoanTypeChangeRD(e) {
    const { target } = e;
    const name = target.getAttribute('data-name');
    const value = target.getAttribute('data-value');
    const fullvalue = target.value;
    const elemName = target.name;
    const precisionProductValue = fullvalue === 'secure_personal_loan'
      ? 'SPL'
      : (fullvalue === 'personal_loan' ? 'UPL' : fullvalue);

    // Reset logic for radio buttons
    if (
      elem.loan_calc_initial_slider_position !== undefined &&
      elem.loan_calc_initial_slider_position !== null &&
      elem.loan_calc_initial_slider_position !== '' &&
      elem.loan_calc_initial_slider_position !== 0
    ) {
      elem.amount = `${elem.loan_calc_initial_slider_position}`;
      elem.isFirstRender = true;
      delete elem.loanDetails;
    }

    elem.updateDetails({ [name]: value });

    // Simulate an event for onAmountChanged
    const simulatedEvent = {
      detail: { value: elem.loan_calc_initial_slider_position },
      target: { dataset: {} },
    };
    elem.onAmountChanged(simulatedEvent);

    elem.sendEvents(e, {
      [elemName]: fullvalue,
      event: 'change',
      label: elemName,
      attr3: fullvalue,
      target,
      section: 'calculator',
    });

    elem.dispatchEvent(new CustomEvent('calculator:field-change', {
      bubbles: true,
      composed: true,
      detail: {
        field: 'product',
        value: precisionProductValue,
        field_label: 'product toggle',
      },
    }));
  }

  function onAmountChanged(e) {
    const target= e.target;
    const { amounts } = elem.loanDetails;
    const validAmounts = amounts.map((a) => Number(a)).filter((n) => !isNaN(n) && n > 0);

    const nearest = getNearestAllowed(parseInt(target.value, 10), validAmounts);
    const value = nearest;
    
    loanSliderUpdate(value, e);
  }

  function getNearestAllowed(val, allowedValues) {
    return allowedValues.reduce((prev, curr) =>
      Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev
    );
  }

  function getTrackPercent(min, max, value){
    return ((value - min) / (max - min)) * 100;
  }

  function rangeKeyboard(e){
    const { amounts } = elem.loanDetails;
    const validAmounts = amounts.map((a) => Number(a)).filter((n) => !isNaN(n) && n > 0);
    const idx = validAmounts.indexOf(parseInt(e.target.value, 10));
    let newIdx = idx;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
      case 'PageUp':
        newIdx = Math.min(validAmounts.length - 1, idx + 1);
        e.preventDefault();
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
      case 'PageDown':
        newIdx = Math.max(0, idx - 1);
        e.preventDefault();
        break;
      case 'Home':
        newIdx = 0;
        e.preventDefault();
        break;
      case 'End':
        newIdx = validAmounts.length - 1;
        e.preventDefault();
        break;
      default:
        return;
    }

    const newVal = validAmounts[newIdx];

    loanSliderUpdate(newVal, e);
  }

  function loanSliderUpdate(value, e){
    const target = e.target;
    elem.updateDetails({ amount: `${value}` });

    target.value = value;
    target.ariaValueNow = value;

    const percent = getTrackPercent(target.min, target.max, value);

    target.style.background = `linear-gradient(to right, #63d391 ${percent}%, #e6e6e6 ${percent}%)`;

    if (target && target.dataset) {
      target.dataset.event_tracking_label = `LoanAmount:${value}`;
    }

    elem.debouncedSendEvents(e, {
      amount: value,
      event: 'change',
      target,
      ...(target ? target.dataset : {}),
    });
  }

  // ----------------- Loan Amounts -----------------
  // filepath: /Users/jose.amezcua/Local Sites/ocw/app/public/wp-content/themes/oportun/src/components/com/calculator/calculator.js
// ...existing code in lines 1-149...

function renderLoanAmounts(details) {
  if (!details) {
    return html`
      <div class="amount-container-preselect-label">${elem.loan_calc_initial_loan_amount_title}</div>
      <div class="amount-container-preselect${elem.inside_hero === 'yes' ? ' hero' : ''}">
        <div class="current_amount">${elem.loan_calc_initial_loan_amount}</div>
      </div>
      <div class="disclosure">${renderDisclosure()}</div>
    `;
  }

  const { amounts, amount } = details;

  if (!Array.isArray(amounts) || amounts.length === 0) {
    console.warn('[Calculator] Amounts array is empty or invalid');
    return html`<div class="error">${msg('Unable to load loan amounts')}</div>`;
  }

  const validAmounts = amounts.map((a) => Number(a)).filter((n) => !isNaN(n) && n > 0);
  
  if (validAmounts.length === 0) {
    console.warn('[Calculator] No valid numeric amounts found');
    return html`<div class="error">${msg('Unable to calculate loan amounts')}</div>`;
  }

  // Determine selected value with proper fallback
  let selected = Number(amount);
  if (elem.isFirstRender) {
    const initialPos = elem.loan_calc_initial_slider_position;
    const hasValidInitial = initialPos !== '' && 
                            initialPos !== undefined && 
                            initialPos !== null && 
                            initialPos !== 0 &&
                            !isNaN(Number(initialPos));
    
    if (hasValidInitial) {
      selected = Number(initialPos);
    } else if (!isNaN(Number(amount)) && Number(amount) > 0) {
      selected = Number(amount);
    } else {
      // Fallback to middle of valid amounts
      const midIndex = Math.floor(validAmounts.length / 2);
      selected = validAmounts[midIndex];
    }
    elem.isFirstRender = false;
  }

  // Ensure selected is a valid number
  if (isNaN(selected) || selected === 0) {
    const midIndex = Math.floor(validAmounts.length / 2);
    selected = validAmounts[midIndex];
  }

  const minAmount = validAmounts[0];
  const maxAmount = validAmounts[validAmounts.length - 1];

  const initialPos = elem.loan_calc_initial_slider_position;
  const hasValidDefault = initialPos !== '' && 
                          initialPos !== undefined && 
                          initialPos !== null && 
                          initialPos !== 0 &&
                          !isNaN(Number(initialPos));
  const sliderDefaultValue = hasValidDefault ? Number(initialPos) : selected;

  return html`
    <div class="amount-container">
      <div class="row">
        <div class="label">${msg('Loan amount')}</div>
        <div class="value"><div class="current_amount">${toUSD(selected)}</div></div>
      </div>
      <div class="row-full">
        <div class="value">
          <input type="range" id="loanAmount" name="loanAmount" list="loanTicks"
            min="${minAmount}" max="${maxAmount}" value="${sliderDefaultValue}"
            aria-valuemin="${minAmount}" aria-valuemax="${maxAmount}" aria-valuenow="${sliderDefaultValue}"
            aria-label="Select loan amount" @input=${onAmountChanged} @keydown="${rangeKeyboard}" tabindex="0"
            data-event_tracking="change" data-event_tracking_label="Amount" data-event_tracking_section="calculator"
            style="background: linear-gradient(to right, var(--green-300) ${getTrackPercent(minAmount, maxAmount, sliderDefaultValue)}%, #e6e6e6 ${getTrackPercent(minAmount, maxAmount, sliderDefaultValue)}%)" />
          <datalist id="loanTicks">
            ${validAmounts.map((amount) => html `<option value="${amount}"></option>`)}
         </datalist>
          
        </div>
      </div>
      <div class="row minmax">
        <div class="min">${toUSD(minAmount)}</div>
        <div class="max">${toUSD(maxAmount)}</div>
      </div>
    </div>
  `;
}


  function renderApr(details) {
    if (!details) return '%';
    const apr = details.apr || {};
    const { min, max } = apr;
    if (min === max) return html`<span>${min}%</span>`;
    return html` <span>${min}</span>-<span>${max}%</span> `;
  }

  function renderPayments(details) {
    if (!details) return '$';
    const payments = details.payments || {};
    const { min, max } = payments;
    if (min === max) return html`<span>${toUSD(max)}</span>`;
    return html`<span>${toUSD(min)}</span>-<span>${toUSD(max)}</span>`;
  }

  function renderDisclosure() {
    const details = elem.loanDetails;
    const disclosure = details && details.disclosure ? details.disclosure : '';
    return html` <div class="disclosure-text">${disclosure}</div> `;
  }

  async function getCreditScores() {
    const metadata = (await getMetaData()) || {};
    const { secure, score: selected = '' } = elem;

    if (!metadata.requireScore) return '';
    if (metadata.hasSPL && secure === null) return '';

    return html`
      <fieldset class="${selected ? 'filled' : ''}">
        <legend id="score-label" aria-hidden="${!selected}">${msg('Estimated credit score')}</legend>
        <select
          name="score"
          id="score"
          @change=${elem.onChange}
          data-event_tracking="change"
          data-event_tracking_label="Credit score"
          data-event_tracking_section="calculator"
          aria-labelledby="score-label"
        >
          <option value="" ?selected=${!selected} ?disabled=${selected}>
            ${msg('Estimated credit score')}
          </option>
          ${scores.map(
            ({ value, label }) =>
              html`<option value="${value}" ?selected=${selected === value}>${label}</option>`
          )}
        </select>
      </fieldset>
    `;
  }

  async function getSecureLoanOptions() {
    const { secure: selected = '' } = elem;
    const metadata = (await getMetaData()) || {};
    if (!metadata.hasSPL) return '';

    return html`
      <fieldset class="${selected ? 'filled' : ''}">
        <legend id="secure-label" aria-hidden="${!selected}">${msg('Do you own a car?')}</legend>
        <select
          name="secure"
          id="secure"
          class="has_car"
          @change=${elem.onChange}
          data-event_tracking="change"
          data-event_tracking_label="Secured"
          data-event_tracking_section="calculator"
          aria-labelledby="secure-label"
        >
          <option value="" ?selected=${!selected} ?disabled=${selected}>
            ${msg('Do you own a car?')}
          </option>
          ${secureOptions.map(
            (opt) => html`<option value="${opt.value}" ?selected=${selected === opt.value}>
              ${opt.label}
            </option>`
          )}
        </select>
      </fieldset>
    `;
  }

  function renderStateSelect(statesArr) {
    const selected = elem.state;
    const locked = !!elem.locked;

    let filteredStates = Array.isArray(statesArr) ? statesArr : [];
    if (elem.AVAILABLE_STATES && elem.AVAILABLE_STATES !== '') {
      filteredStates = filteredStates.filter((st) => elem.AVAILABLE_STATES.indexOf(st) !== -1);
    }

    const sortedStates = filteredStates.sort((a, b) => a.localeCompare(b));

    return html`
      <fieldset class="${selected ? 'filled' : ''}">
        <legend id="state-label" aria-hidden="${!selected}">${msg('Where do you live?')}</legend>
        <select
          name="state"
          id="state"
          ?disabled=${locked}
          @change=${elem.onChange}
          data-event_tracking="change"
          data-event_tracking_label="States"
          data-event_tracking_section="calculator"
          aria-labelledby="state-label"
        >
          <option value="" ?selected=${!selected} ?disabled=${selected}>
            ${msg('Where do you live?')}
          </option>
          ${sortedStates.map(
            (stateName) =>
              html`<option value=${stateName} ?selected=${selected === stateName}>
                ${stateName}
              </option>`
          )}
        </select>
      </fieldset>
    `;
  }



  function renderLoanTypeSelector(details) {
  if (!details) return '';

  const { meta, type, secure, amounts } = details;
  const hasSPL = !!(meta && meta.hasSPL);
  if (!hasSPL) return '';
  if (secure !== 'yes') return '';

  const { upl, spl } = meta || {};
  if (!upl || !spl) return '';
  if (!Array.isArray(amounts) || !amounts.length) return '';

  const amountsNum = amounts.map((a) => +a);
  const minAmt = amountsNum[0];
  const maxAmt = amountsNum[amountsNum.length - 1];

  // Determine active loan type with proper default
  const activeType = type || 'upl';
  const isUpl = activeType === 'upl';
  const isSpl = activeType === 'spl';

  function renderButtons() {
    return html`
      <div class="row loan-types">
        <button
          class="upl-loan ${isUpl ? 'selected' : ''}"
          data-name="type"
          data-value="upl"
          data-event_tracking="Button click"
          data-event_tracking_label="type upl"
          data-event_tracking_section="calculator"
          @click=${onLoanTypeChange}
          ?selected=${isUpl}
        >
          <span class="loan-type">${msg('Personal loan')}</span>
          <span class="range">(${toUSD(upl.min)}-${toUSD(upl.max)})</span>
        </button>
        <span class="or">${msg('or')}</span>
        <button
          class="spl-loan ${isSpl ? 'selected' : ''}"
          data-name="type"
          data-value="spl"
          data-event_tracking="Button click"
          data-event_tracking_label="type spl"
          data-event_tracking_section="calculator"
          @click=${onLoanTypeChange}
          ?selected=${isSpl}
        >
          <span class="loan-type">${msg(' Secured personal loan')}</span>
          <span class="range">(${toUSD(spl.min)}-${toUSD(spl.max)})</span>
        </button>
      </div>

      <div class="row banner ${isSpl ? 'active' : ''}" aria-hidden="${!isSpl}">
        <div class="spl-loan-advantage">
          <div class="icon"><oportun-icon name="info"></oportun-icon></div>
          <div class="text">
            ${msg('Use your car title for better odds of getting a larger loan.')}
          </div>
        </div>
      </div>
    `;
  }

  function renderRadioButtons() {
    return html`
      <div class="loan-types-legend">${msg('Apply once and get two great options')}</div>
      <div class="loan-types radio">
        <input
          type="radio" 
          id="upl" 
          name="loan_type"
          class="upl-loan ${isUpl ? 'selected' : ''}"
          data-name="type" 
          data-value="upl" 
          value="personal_loan"
          data-event_tracking="change" 
          data-event_tracking_label="radio_button"
          data-event_tracking_attr3="personal_loan" 
          data-event_tracking_section="calculator"
          @change=${onLoanTypeChangeRD} 
          ?checked=${isUpl}
        />
        <label for="upl">
          <span class="loan-type">${msg('Personal loans')}</span>
          <span class="range">${msg('Max of ')} ${toUSD(upl.max)}</span>
        </label>

        <span class="or"></span>

        <input
          type="radio" 
          id="spl" 
          name="loan_type"
          class="spl-loan ${isSpl ? 'selected' : ''}"
          data-name="type" 
          data-value="spl" 
          value="secure_personal_loan"
          data-event_tracking="change" 
          data-event_tracking_label="radio_button"
          data-event_tracking_attr3="secure_personal_loan" 
          data-event_tracking_section="calculator"
          @change=${onLoanTypeChangeRD} 
          ?checked=${isSpl}
        />
        <label for="spl">
          <span class="loan-type">${msg(' Secured personal loans')}</span>
          <span class="range">${msg('Max of ')} ${toUSD(spl.max)}</span>
        </label>
      </div>

      <div class="row banner ${isSpl ? 'active' : ''}" aria-hidden="${!isSpl}">
        <div class="spl-loan-advantage">
          <div class="icon"><oportun-icon name="info"></oportun-icon></div>
          <div class="text">
            ${msg('Use your car title for better odds of getting a larger loan.')}
          </div>
        </div>
      </div>
    `;
  }

  const useButtons = elem.upl_spl_type === 'buttons';
  return html`${useButtons ? renderButtons() : renderRadioButtons()}`;
  }

  function renderHeader(details) {
    if (!details) return '';
    return html``; // placeholder preserved
  }

  function paymentFrequency() {
    if (state === 'New Jersey') {
      return msg('once a month');
    }
    return msg('every two weeks');
  }

  function renderDetails() {
    const details = elem.loanDetails;
    if (details && elem.inside_hero == 'yes' && !isTouch()) {
      elem.parent.style.setProperty('grid-template-columns', '100%');
      if (elem.parent.firstElementChild.nodeName.toLowerCase() != 'oportun-loan-calculator') {
        elem.getRootNode().host.style.setProperty('grid-area', 'c1');
        elem.parent.firstElementChild.style.setProperty('display', 'none');
      }
    }

    const mobile = isMobile();
    if (!mobile) {
      document.addEventListener('click', heroCalcClose);
    }

    return html`
      <div class="loan-details-container${elem.inside_hero == 'yes' ? ' hero' : ''}">
        ${renderHeader(details)}
        ${renderLoanAmounts(details)}
        ${details
          ? html`
              <div role="list">
                <div class="rows" role="listitem">
                  <div class="label">
                    <strong>${msg('Pay ')} ${paymentFrequency()}</strong>
                  </div>
                  <div class="value">${renderPayments(details)}</div>
                </div>
                <div class="rows" role="listitem">
                  <div class="label">
                    <strong>APR</strong>
                  </div>
                  <div class="value">${renderApr(details)}</div>
                </div>
                <div class="rows" role="listitem">
                  <div class="label">
                    <strong>${msg('Number of payments')}</strong>
                  </div>
                  <div class="value">${details ? details.noOfPayments : '#'}</div>
                </div>
                <div class="rows hide-border-mobile" role="listitem">
                  <div class="label">
                    <label for="duration"><strong>${msg('Length of loan')}</strong></label>
                  </div>
                  <div class="value">
                    <span class="custom-select">
                      <select
                        name="duration"
                        id="duration"
                        @change=${onLoanDetailsChange}
                        data-event_tracking="change"
                        data-event_tracking_label="duration"
                        data-event_tracking_section="calculator"
                      >
                        ${(details.durations || []).map(
                          (dur) =>
                            html`<option value=${dur} ?selected=${details.duration === dur}>
                              ${dur}
                            </option>`
                        )}
                      </select>
                    </span>
                  </div>
                </div>
              </div> 
              <div class="loan-details-container-bottom">
                <div class="disclosure">${renderDisclosure()}</div>
                <div class="calltoaction">${renderCallToAction()}</div>
              </div>
            `
          : ''}
      </div>
    `;
  }

  function renderCaption() {
    const details = elem.loanDetails;

    async function getCaption() {
      const meta = (await getMetaData()) || {};
      if (meta.hasSPL) {
        return '';
      }
      return '';
    }
    if (details) return '';
    return until(getCaption(), () => '');
  }

  function renderCallToAction() {
    if (elem.disable_apply) return '';
    return html`
      <div class="apply-button">
        <a
          href="${elem.loan_calc_cta_url}"
          @mousedown=${elem.handleApplyBtn}
          class="button"
          data-event_tracking="button click"
          data-event_tracking_section="calculator"
          data-event_tracking_label="Start your loan application"
          >${elem.loan_calc_cta_text}</a
        >
      </div>
    `;
  }

  function renderRetailLoanTable(details) {
    if (!details) return html``;
    const {
      topDisclosure,
      bottomDisclosure,
      rightDisclosure,
      amountFinanced,
      adminFees,
      latePaymentFee,
      returnedPaymentFee,
    } = details;

    return html`
      <div class="retail-disclaimer">${topDisclosure}</div>
      <div class="loan-calc-table-of-concepts">
        <div class="col1">
          <div class="cell">${msg('Amount financed')}</div>
          <div class="cell">${msg('Administrative fees')}</div>
          <div class="cell">${msg('Late payment fee')}</div>
          <div class="cell">${msg('Returned payment fee')}</div>
        </div>
        <div class="col2">
          <div class="cell">${amountFinanced}</div>
          <div class="cell">${adminFees}</div>
          <div class="cell">${latePaymentFee}</div>
          <div class="cell">${returnedPaymentFee}</div>
        </div>
        <div class="col3">
          <div class="cell">${rightDisclosure}</div>
        </div>
      </div>
      <div class="retail-disclaimer">${bottomDisclosure}</div>
    `;
  }

  function renderCalculator() {
    const details = elem.loanDetails;
    const tofade = elem.pre_populate == 'yes' ? 'yes' : 'no';
    return html`
      ${tofade == 'yes'
        ? html`<div class="spinner-bg">
            <div class="spinner-text">${msg('Loading')}</div>
            <div class="spinner"></div>
          </div>`
        : ''}
      <div
        class="calculator ${tofade == 'yes' ? 'animate-bottom' : ''} "
        style="${tofade == 'yes' ? 'display:none;' : ''}"
      >
        <div class="title">
          <slot name="title"></slot>
        </div>
        <div class="user-details">
          <div class="desc">
            <slot name="description"></slot>
          </div>

          ${renderCaption()}
          <div class="user-fields">
            <div class="field state">${renderStateSelect(states)}</div>
            <div class="field secure">${until(getSecureLoanOptions(), '')}</div>
            <div class="field credit-scores">${until(getCreditScores(), '')}</div>
          </div>

          ${renderLoanTypeSelector(details)}
        </div>
        <div class="loan-details">${renderDetails()}</div>
      </div>
      <div name="retail-table" class="retail-table">
        ${elem.loan_calc_style ? renderRetailLoanTable(details) : ''}
      </div>
    `;
  }

  function heroCalcClose(e) {
    const details = elem.loanDetails;
    const heroCalc = document.querySelector('oportun-loan-calculator[inside_hero="yes"]');
    if (details && heroCalc && !heroCalc.contains(e.target)) {
      const stateDropdown = elem.shadowRoot?.querySelector?.('[name="state"]');
      if (stateDropdown) {
        stateDropdown.selectedIndex = 0;
        elem.loanDetails = null;
        elem.state = null;
        elem.triggetUpd();
        renderCalculator();
      }
      elem.parent.style.setProperty('grid-template-columns', 'var(--cgrid-columns)');
      if (elem.parent.firstElementChild.nodeName.toLowerCase() != 'oportun-loan-calculator') {
        elem.getRootNode().host.style.setProperty('grid-area', 'c2');
        elem.parent.firstElementChild.style.setProperty('display', 'block');
      }
    }
  }

  return html` ${states ? renderCalculator() : html`<div>loading...</div>`} `;
}

function isMobile() {
  let hasTouchScreen = false;
  if ('maxTouchPoints' in navigator) {
    hasTouchScreen = navigator.maxTouchPoints > 0;
  } else if ('msMaxTouchPoints' in navigator) {
    hasTouchScreen = navigator.msMaxTouchPoints > 0;
  } else {
    const mQ = matchMedia?.('(pointer:coarse)');
    if (mQ?.media === '(pointer:coarse)') {
      hasTouchScreen = !!mQ.matches;
    } else if ('orientation' in window) {
      hasTouchScreen = true; // deprecated, but good fallback
    } else {
      const UA = navigator.userAgent;
      hasTouchScreen =
        /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
        /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA);
    }
  }
  return hasTouchScreen;
}

export default class Calculator extends NorthStarElement {
  static styles = styles;

  static template = template;

  static properties = {
    statesList: { type: Object },
    parent: { type: Object },
    state: { type: String },
    score: { type: String },
    duration: { type: String },
    amount: { type: String },
    secure: { type: String },
    type: { type: String },
    loanDetails: { type: String },
    loantype: { type: String },
    locked: { type: String }, // lock to one state
    disable_apply: { type: Boolean },
    upl_spl_type: { type: String },
    loan_calc_style: { type: String },
    loan_calc_cta_text: { type: String },
    loan_calc_cta_url: { type: String },
    loan_calc_initial_loan_amount: { type: String },
    loan_calc_initial_loan_amount_title: { type: String },
    loan_calc_initial_slider_position: { type: Number },
    getCurrLocation: { type: String },
    pre_populate: { type: String },
    inside_hero: { type: String },
    CALC_DATA: { type: Object },
    MAXMIN_DATA: { type: Object },
    AVAILABLE_STATES: { type: Object },
    lang: { type: String },
    isFirstRender: { type: Boolean },
  };

  constructor() {
    super();
    this.isFirstRender = true;

    // throttle analytics from slider drag
    this.debouncedSendEvents = debounce((e, data) => {
      this.sendEvents(e, data);
    }, 1000);
  }

  getStateMetaData() {
    const { state } = this;
    return this.api.getStateMetaData(state);
  }

  async onChange(e) {
    const { target } = e;
    const { name, value } = target;

    if (name === 'state') {
      // reset when state changes
      this.score = null;
      this.secure = null;
      try {
        await this.fetchStateData(value);
        this.api = createApi(this.CALC_DATA, this.MAXMIN_DATA);
      } catch (error) {
        console.error(error);
      }
      delete this.loanDetails;
    }

    if (name === 'secure') {
      // reset when secure changes
      this.score = null;
      this.secure = null;
      delete this.loanDetails;
    }

    if (
      this.loan_calc_initial_slider_position !== undefined &&
      this.loan_calc_initial_slider_position !== null &&
      this.loan_calc_initial_slider_position !== '' &&
      this.loan_calc_initial_slider_position !== 0
    ) {
      this.amount = `${this.loan_calc_initial_slider_position}`;
      this.isFirstRender = true;
      delete this.loanDetails;

      // Simulate an event for onAmountChanged
      const simulatedEvent = {
        detail: { value: this.loan_calc_initial_slider_position },
        target: { dataset: {} },
      };
      this.onAmountChanged(simulatedEvent);
    }

    this[name] = value;
    await this.fetchLoanDetails();

    this.sendEvents(e, {
      [name]: value,
      event: 'change',
      label: name,
      target,
      section: 'calculator',
    });

    this.dispatchEvent(new CustomEvent('calculator:field-change', {
      bubbles: true,
      composed: true,
      detail: { field: name, value },
    }));
  }

  handleApplyBtn(e) {
    this.sendAnalyticsEvent({
      target: e.currentTarget,
      originalEvent: e,
      data: {
        ...e.currentTarget.dataset,
      },
    });
  }

  async fetchStateData(state) {
    const stateFile = this.stateSlug(state);
    const jsonURL = `${window.location.origin}/wp-content/uploads/loan_calc/${this.lang}/${stateFile}.json`;

    try {
      const response = await fetch(jsonURL);
      if (!response.ok) {
        throw new Error(`Failed to load JSON: ${response.status} ${response.statusText}`);
      }
      const jsonData = await response.json();
      if (!jsonData || typeof jsonData !== 'object') {
        throw new Error('Invalid JSON format');
      }
      this.CALC_DATA = jsonData;
    } catch (error) {
      console.error('Error:', error);
    }
  }

  stateSlug(stateName) {
    let stateSlug = stateName.trim().toLowerCase();
    stateSlug = stateSlug.replace(/\s+/g, '-');
    stateSlug = stateSlug.replace(/[^a-z0-9-áéíóúüñ]/g, '');
    stateSlug = stateSlug.replace(/^-+|-+$/g, '');
    return stateSlug;
  }

  async fetchLoanDetails() {
    const { state, score, secure } = this;
    if (!state || !this.api) return null;

    const { amount, duration, type } = this.loanDetails || {};
    try {
      const params = { state, score, secure, amount, duration, type };
      const details = await this.api.getLoanDetails(params);
      this.loanDetails = details || null;
      return this.loanDetails;
    } catch (e) {
      this.loanDetails = null;
      return null;
    }
  }

  updateDetails(params) {
    if (!this.loanDetails) this.loanDetails = {};
    Object.assign(this.loanDetails, params);
    this.fetchLoanDetails();
  }

  getStateNames() {
    return this.api.getStateNames();
  }

  // hook kept for parity
  updateBanner(_node) {}

  sendEvents(e, data) {
    const { target = e.target } = data || {};
    if (!target) return;

    const { nodeName } = target;
    let targetNode = target;

    // wrap custom elements into an input proxy for trackers
    if (nodeName && nodeName.toUpperCase().startsWith(NAMESPACE.toUpperCase())) {
      const proxy = document.createElement('input');
      proxy.setAttribute('type', 'text');
      proxy.setAttribute('value', target.value);
      for (const { name, value } of target.attributes) {
        proxy.setAttribute(name, value);
      }
      data.target = proxy;
      targetNode = proxy;
    }

    this.sendAnalyticsEvent({ target: targetNode, originalEvent: e, data });
  }

  triggetUpd() {
    if (typeof this.getCurrLocation !== 'undefined' && this.getCurrLocation != null) {
      const selectState = this.shadowRoot?.querySelector?.('[name="state"]');
      if (selectState) {
        selectState.value = this.getCurrLocation;
        selectState.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  processTealiumLocation(locationString) {
    const ret = {};
    if (typeof locationString === 'string') {
      const locPieces = locationString.split(',');
      locPieces.forEach((piece) => {
        const tmp = piece.split('=');
        if (tmp[0]) ret[tmp[0]] = tmp[1];
      });
      return ret;
    }
    return ret;
  }

  attachedCallback() {
    this.parent = this.getRootNode().host.parentNode;
    this.inside_hero = this.getRootNode().host.getAttribute('inside_hero');
    this.lang = this.getRootNode().host.getAttribute('lang');

    const availableEl = this.getRootNode().host.querySelector('#available_states');
    const maxminEl = this.getRootNode().host.querySelector('#max_min_data');
    if (availableEl) this.AVAILABLE_STATES = JSON.parse(availableEl.value);
    if (maxminEl) this.MAXMIN_DATA = JSON.parse(maxminEl.value);

    if (this.pre_populate == 'yes') {
      const currentLocation = () => {
        // utag_data expected from Tealium; guard usage
        // eslint-disable-next-line no-undef
        if (typeof utag_data?.visitor_state_location !== 'undefined') {
          // eslint-disable-next-line no-undef
          let currLocation = utag_data.visitor_state_location;
          if (typeof currLocation !== 'undefined' && currLocation != null) {
            const parsed = this.processTealiumLocation(currLocation);
            if (parsed.region_code == 'NLE') {
              this.getCurrLocation = 'California'; // testing from Agu - Mex
            } else {
              this.getCurrLocation = StatesMap[parsed.region_code];
            }
          }

          this.triggetUpd();

          let timeoutSecureDrop;
          clearTimeout(timeoutSecureDrop);
          timeoutSecureDrop = setTimeout(() => {
            const selectSecure = this.shadowRoot?.querySelector?.('[name="secure"]');
            if (selectSecure) {
              selectSecure.value = 'yes';
              selectSecure.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }, 800);

          let timeoutScoreDrop;
          clearTimeout(timeoutScoreDrop);
          timeoutScoreDrop = setTimeout(() => {
            const selectScore = this.shadowRoot?.querySelector?.('[name="score"]');
            if (selectScore) {
              selectScore.value = 'Exceptional (750-850)';
              selectScore.dispatchEvent(new Event('change', { bubbles: true }));
            }
            const spinner = this.shadowRoot?.querySelector?.('.spinner-bg');
            const calc = this.shadowRoot?.querySelector?.('.calculator');
            if (spinner) spinner.style.display = 'none';
            if (calc) calc.style.display = '';
          }, 1000);

          return this.getCurrLocation;
        }
        return false;
      };
      window.addEventListener('load', currentLocation);
    }
  }

  manualConnect() {
    this.connectedCallback();
    this.attachedCallback();
  }

  async connectedCallback() {
    super.connectedCallback();

    const availableEl = this.getRootNode().host.querySelector('#available_states');
    const maxminEl = this.getRootNode().host.querySelector('#max_min_data');
    if (availableEl) this.AVAILABLE_STATES = JSON.parse(availableEl.value);
    if (maxminEl) this.MAXMIN_DATA = JSON.parse(maxminEl.value);

    this.api = createApi(this.CALC_DATA, this.MAXMIN_DATA);

    this.statesList = [
      'Alabama',
      'Alaska',
      'Arizona',
      'Arkansas',
      'California',
      'Delaware',
      'Florida',
      'Georgia',
      'Hawaii',
      'Idaho',
      'Illinois',
      'Indiana',
      'Kansas',
      'Kentucky',
      'Louisiana',
      'Michigan',
      'Minnesota',
      'Mississippi',
      'Missouri',
      'Montana',
      'Nebraska',
      'Nevada',
      'New Hampshire',
      'New Jersey',
      'New Mexico',
      'North Dakota',
      'Ohio',
      'Oklahoma',
      'Oregon',
      'Pennsylvania',
      'Rhode Island',
      'South Carolina',
      'South Dakota',
      'Tennessee',
      'Texas',
      'Utah',
      'Vermont',
      'Virginia',
      'Wisconsin',
      'Wyoming',
    ];
  }

  onAmountChanged(e) {
    const { target } = e || {};
    const { value } = e?.detail || {};

    if (value == null) return;

    this.updateDetails({ amount: `${value}` });

    if (target && target.dataset) {
      target.dataset.event_tracking_label = `LoanAmount:${value}`;
    }

    this.debouncedSendEvents(e, {
      amount: value,
      event: 'change',
      target,
      ...(target ? target.dataset : {}),
    });
  }
}

customElements.define(`${NAMESPACE}-calculator`, Calculator);
